import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";

// Ahora la imagen se usará como fondo
import PagoEnvioImg from "../../static/media/home/transporte.webp";
import BancoIcon from "../../static/media/home/Banco.webp";
import EnvioIcon from "../../static/media/home/Envio.webp";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  navyDark: "#003D73",
  gold: "#FFCD00",
  teal: "#5BC8AF",
  gray: "#F5F7FA",
  white: "#ffffff",
};

const WHATSAPP_URL =
  "https://api.whatsapp.com/send/?phone=593987724998&text=Hola+quiero+m%C3%A1s+informaci%C3%B3n&type=phone_number&app_absent=0";

const BankIcon = () => (
  <Box
    component="img"
    src={BancoIcon}
    alt="Banco"
    sx={{ width: 54, height: 54, objectFit: "contain" }}
  />
);
const TruckIcon = () => (
  <Box
    component="img"
    src={EnvioIcon}
    alt="Envío"
    sx={{ width: 54, height: 54, objectFit: "contain" }}
  />
);
const WhatsAppSVG = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.87.5 3.62 1.43 5.13L2 22l5.13-1.5a9.86 9.86 0 0 0 4.91 1.31c5.46 0 9.91-4.45 9.91-9.9C21.95 6.45 17.5 2 12.04 2zm0 17.9c-1.62 0-3.13-.45-4.42-1.24l-.32-.19-3.04.89.9-2.96-.21-.32a8.06 8.06 0 0 1-1.27-4.17c0-4.46 3.63-8.09 8.36-8.09 4.46 0 8.09 3.63 8.09 8.09 0 4.46-3.63 8-8.09 8zm4.46-6.05c-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.55.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.92-1.19-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.11-.49.12-.12.27-.31.4-.47.13-.16.18-.27.27-.45.09-.18.04-.33-.04-.45-.08-.12-.5-1.21-.69-1.65-.18-.43-.37-.37-.51-.38-.13-.01-.28-.01-.43-.01-.15 0-.39.06-.6.3-.21.24-.8.78-.8 1.9s.82 2.21.93 2.36c.11.15 1.58 2.41 3.83 3.29 2.25.88 2.25.59 2.66.55.41-.04 1.32-.54 1.5-1.06.18-.52.18-.97.13-1.06-.05-.09-.18-.15-.42-.27z" />
  </svg>
);

function HorizontalCard({ icon, title, children, delay = 0 }) {
  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: "easeOut", delay }}
      sx={{
        background: "rgba(255, 255, 255, 0.95)", // Blanco muy sutilmente transparente
        backdropFilter: "blur(10px)",
        borderRadius: "24px",
        p: { xs: "24px 20px", md: "28px" },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 2.5, md: 3 },
        boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        transition: "transform 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 64, sm: 80 },
          height: { xs: 64, sm: 80 },
          borderRadius: "18px",
          background: `linear-gradient(135deg, ${C.gray} 0%, #E8EDF2 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          border: "1px solid rgba(12, 31, 47, 0.05)",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography
          sx={{
            color: C.dark,
            fontWeight: 700,
            fontSize: { xs: 18, md: 20 },
            mb: 1,
            letterSpacing: "-.01em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

export default function PagoEnvios() {
  return (
    <Box
      id="pago-envios"
      sx={{
        position: "relative",
        minHeight: "100vh", // Asegura que ocupe el tamaño de la pantalla
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        py: { xs: 6, md: 0 }, // Padding solo en móviles por si el contenido es muy alto
      }}
    >
      {/* IMAGEN DE FONDO */}
      <Box
        component="img"
        src={PagoEnvioImg}
        alt="Logística y Pagos"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      />

      {/* OVERLAY CORPORATIVO */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          // Degradado que va de un azul muy oscuro a uno más claro transparente
          background: `linear-gradient(90deg, rgba(12, 31, 47, 0.92) 0%, rgba(0, 87, 168, 0.75) 100%)`,
          zIndex: 1,
        }}
      />

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          width: "100%",
          px: { xs: "24px", sm: "40px", md: "56px" },
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "center",
          gap: { xs: 6, lg: 8 },
          boxSizing: "border-box",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* LADO IZQUIERDO: TÍTULO Y TEXTO */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            width: { xs: "100%", lg: "45%" },
            textAlign: { xs: "center", lg: "left" },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: C.white,
              fontWeight: 750,
              fontSize: { xs: 28, sm: 34, md: 40 },
              letterSpacing: "-.02em",
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            Pago Fácil, <br sx={{ display: { xs: "none", md: "block" } }} />
            <Box component="span" sx={{ color: C.gold }}>
              Envíos Seguros
            </Box>
          </Typography>
          <Box
            sx={{
              width: "50px",
              height: "3px",
              background: C.gold,
              mx: { xs: "auto", lg: 0 },
              borderRadius: "2px",
              mb: 3,
            }}
          />
          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.6,
              maxWidth: "500px",
              mx: { xs: "auto", lg: 0 },
            }}
          >
            Optimizamos tu proceso de compra. Confirma tu pedido rápidamente y
            recíbelo donde lo necesites con total garantía y respaldo.
          </Typography>
        </Box>

        {/* LADO DERECHO: TARJETAS */}
        <Box
          sx={{
            width: { xs: "100%", lg: "55%" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: "20px", md: "24px" },
          }}
        >
          <HorizontalCard
            icon={<BankIcon />}
            title="Transferencia Bancaria"
            delay={0.1}
          >
            <Typography
              sx={{
                color: "#5c6c7a",
                fontSize: { xs: 13.5, md: 14.5 },
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Un medio rápido y seguro para confirmar tu pedido sin
              complicaciones. ¿Necesitas los datos de cuenta o tienes dudas?
            </Typography>

            <Box sx={{ mt: "auto", display: "flex" }}>
              <Box
                component={motion.a}
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  textDecoration: "none",
                  background: C.teal,
                  color: C.dark,
                  fontWeight: 700,
                  fontSize: 13.5,
                  px: "20px",
                  py: "10px",
                  borderRadius: "999px",
                  boxShadow: "0 6px 16px rgba(91, 200, 175, 0.35)",
                  transition: "background .2s",
                  "&:hover": { background: "#4ab59e" },
                }}
              >
                <WhatsAppSVG />
                Contactar por WhatsApp
              </Box>
            </Box>
          </HorizontalCard>

          <HorizontalCard
            icon={<TruckIcon />}
            title="Cobertura de Envío"
            delay={0.25}
          >
            <Typography
              sx={{
                color: "#5c6c7a",
                fontSize: { xs: 13.5, md: 14.5 },
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Recibe tu pedido estés donde estés. Contamos con logística
              especializada para material eléctrico.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
                mt: "auto",
              }}
            >
              {[
                {
                  num: "24h",
                  label: "Despacho Quito",
                  bg: C.gray,
                  color: C.navy,
                },
                {
                  num: "48h",
                  label: "Resto del país",
                  bg: "rgba(0, 87, 168, 0.06)",
                  color: C.navyDark,
                },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    background: stat.bg,
                    borderRadius: "14px",
                    p: "14px",
                    textAlign: "center",
                    border: "1px solid rgba(12, 31, 47, 0.04)",
                  }}
                >
                  <Typography
                    sx={{
                      color: stat.color,
                      fontWeight: 700,
                      fontSize: { xs: 20, md: 24 },
                      lineHeight: 1,
                      mb: 0.5,
                    }}
                  >
                    {stat.num}
                  </Typography>
                  <Typography
                    sx={{ color: "#5c6c7a", fontSize: 11.5, fontWeight: 600 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </HorizontalCard>
        </Box>
      </Box>
    </Box>
  );
}
